import { MUICheckBox } from "@abb/arcadia-ui-react"
import { MouseEvent, useCallback, useContext, useMemo, useState } from "react"
import { useDeleteConfirm } from "../../../../hooks/useDeleteConfirm"
import { useUsers } from "../../../../hooks/useUsers"
import { DiMaProgress } from "../../../components/DiMaProgress"
import { UserContext } from "../../../context/UserContext"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToFeature,
} from "../../../models/Authorization"
import { UserGet } from "../../../models/User"
import { deleteEntity } from "../../../utils/deleteEntity"
import { DiMaAddButton } from "../../partials/DiMaAddButton"
import { EntityRow, EntityTab } from "../../partials/EntityTab"

export function Users() {
  const [onlyWithBackofficeAccess, setOnlyWithBackofficeAccess] = useState(false)
  const { usersList, isLoadingUsers } = useUsers(onlyWithBackofficeAccess)
  const { user, isLoadingUser } = useContext(UserContext)
  const { requestDelete, DeleteConfirmDialog } = useDeleteConfirm()

  const onChange = useCallback(() => {
    setOnlyWithBackofficeAccess((backOffice) => !backOffice)
  }, [])

  const handleToggleBackofficeCheckbox = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation()
      onChange()
    },
    [onChange]
  )

  const cols = [
    {
      field: "username",
      headerName: "Email address",
      isLink: true,
      linkBasePath: "/users",
    },
    {
      field: "fullName",
      headerName: "Full name",
    },
  ]

  const rows: EntityRow[] = usersList.map((userDetail: UserGet) => ({
    id: userDetail.id.toString(),
    username: userDetail.username,
    fullName: `${userDetail.first ?? ""} ${userDetail.last ?? ""}`,
    deletePath: `/users/${userDetail.id}`,
  }))

  const hasAccessToBackOffice = hasUserAccessToFeature(
    user?.authorizations ?? { applications: [], features: [] },
    DimaFeatureShortNameEnum.BA,
    AccessLevel.Read
  )

  const toolbar = useMemo(
    () => (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: hasAccessToBackOffice ? "space-between" : "flex-end",
          gap: 16,
          width: "100%",
        }}
      >
        {hasAccessToBackOffice && (
          <MUICheckBox
            checked={onlyWithBackofficeAccess}
            label="Only with backoffice access"
            size={16}
            handleToggle={handleToggleBackofficeCheckbox}
          />
        )}
        <DiMaAddButton url="/users/create" feature={DimaFeatureShortNameEnum.RM} />
      </div>
    ),
    [hasAccessToBackOffice, onlyWithBackofficeAccess, handleToggleBackofficeCheckbox]
  )

  const handleActionMenuClick = useCallback(
    (item: { title: string; iconName?: string }, row: EntityRow) => {
      if (item.title === "Delete") {
        requestDelete(() => deleteEntity(row.deletePath as string))
      }
    },
    [requestDelete]
  )

  if (isLoadingUser || !user) {
    return <DiMaProgress />
  }

  return (
    <>
      {DeleteConfirmDialog}
      <EntityTab
        rows={rows}
        cols={cols}
        isLoading={isLoadingUsers || isLoadingUser}
        toolbar={toolbar}
        actionItems={[{ title: "Delete", iconName: "icon_abb_dias icon-abb_delete" }]}
        onActionMenuClick={handleActionMenuClick}
      />
    </>
  )
}
